import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Stack
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import PermissionGuard from "../../../../../components/PermissionGuard";
import ScreenBanner from "../../../../../components/ScreenBanner";
import {
    PesquisarSolicitacaoCompraResponse,
    SolicitacaoCompra
} from "../../../../../types/api/estoque/pesquisar-solicitacao-compra";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import { ModalDetalheSolicitacaoProvider, useModalDetalheSolicitacao } from "../ModalDetalheSolicitacaoContext";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import DialogListarSolicitacao from "./components/DialogListarSolicitacao";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";
import { Status } from "./components/Status";
import { processUrlFilters } from "../../../../../components/TableDataGridV2/helpers";
import { TFilters } from "../../../../../components/TableDataGridV2/types";


function ListarSolicitacaoContent() {

    const { openModal } = useModalDetalheSolicitacao();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL();

    const [filtros, setFiltros] = useState<TFiltroListarSolicitacao>({});
    const [openFiltros, setOpenFiltros] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);
    const debouncedKeyword = useDebounce(filtros.palavraChave ?? '', 1000);
    const inputRef = useRef<HTMLInputElement>(null);

    const queryParams = useMemo(() => {
        const params: Record<string, any> = {
            page: page + 1,
            limit,
        };

        if (debouncedKeyword) {
            params.pesquisar = debouncedKeyword;
        }

        if (filtros.status && filtros.status.length > 0) {
            params.status = Array.isArray(filtros.status)
                ? filtros.status.join(',')
                : filtros.status;
        }

        return params;
    }, [debouncedKeyword, filtros, page, limit]);

    const { data, isLoading } = useQuery<PesquisarSolicitacaoCompraResponse>({
        queryKey: ['listar-solicitacoes-compras', queryParams],
        queryFn: async () => {
            const response = await axiosClient.get("/estoque/compras/pesquisar-solicitacoes", {
                params: queryParams
            });
            return response.data;
        },
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    const solicitacoes = data?.data || [];
    const total = data?.total || 0;

    const handleSearch = useCallback((pesquisa: string) => {
        const urlFilters = processUrlFilters({}, searchParams);
        urlFilters.pesquisar = pesquisa || undefined;
        setMultipleParamsUrl(urlFilters);
        setFiltros((prevState) => ({ ...prevState, palavraChave: pesquisa }));
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleApplyFilters = (newFilters: TFiltroListarSolicitacao) => {
        const novoObjeto: TFilters = { ...newFilters };
        Object.keys(newFilters).forEach((key) => {
            if (key.startsWith('status')) {
                novoObjeto.status = [novoObjeto.status];
            }
            if (key.startsWith('palavraChave')) {
                novoObjeto.pesquisar = novoObjeto.palavraChave;
                delete novoObjeto.palavraChave;
            }
        });

        setMultipleParamsUrl(novoObjeto);

        setFiltros(newFilters);
        setPage(0);
    }

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['palavraChave']
        );
    }, [filtros]);

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };
        const urlFilters = processUrlFilters({}, searchParams);

        if (item.startsWith('Status:')) {
            delete newFiltros.status;
            delete urlFilters.status;
        } else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TFiltroListarSolicitacao];
                }
            });
        }

        setMultipleParamsUrl(urlFilters);
        setFiltros(newFiltros);
    }

    useEffect(() => {
        const urlFilters = processUrlFilters({}, searchParams);
        if (urlFilters && Object.keys(urlFilters).length > 0) {
            const filtrosFromUrl: TFiltroListarSolicitacao = {};
            if (urlFilters.pesquisar) {
                filtrosFromUrl.palavraChave = urlFilters.pesquisar;
            }
            if (urlFilters.status) {
                filtrosFromUrl.status = urlFilters.status;
            }

            setFiltros(filtrosFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ background: "#F5F6FA", minHeight: "100vh", py: 4 }}>
            <Container maxWidth="lg">
                <Typography textAlign="center" variant="h5" mb={3}>
                    Listagem de solicitações
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ width: "100%", overflowX: "auto" }}>
                                <SearchWithFilters
                                    palavraChave={filtros.palavraChave ?? ''}
                                    textFieldProps={{
                                        inputRef: inputRef,
                                        label: "Pesquisar",
                                        placeholder: "Pesquise por número da solicitação",
                                        size: "medium",
                                    }}
                                    pesquisar={handleSearch}
                                    setTermoPesquisa={handleSearch}
                                    handleOpenFilters={() => setOpenFiltros(true)}
                                />

                                <ChipList
                                    items={chipItems}
                                    onDelete={handleDeleteFilter}
                                />

                                {isLoading ? (
                                    <Box p={3}>
                                        <Typography>Carregando...</Typography>
                                    </Box>
                                ) : solicitacoes.length === 0 ? (
                                    <DisplayNotFoundInfo label="Nenhuma solicitação encontrada" />
                                ) : (
                                    <>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width="40%"><b>Solicitação</b></TableCell>
                                                    <TableCell width="30%"><b>Número de produtos</b></TableCell>
                                                    <TableCell width="30%" align="right"><b>Status</b></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {solicitacoes.map((row: SolicitacaoCompra) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell>
                                                            <Box sx={{ cursor: 'pointer' }} onClick={() => {
                                                                setParamsUrl(row.codigo.toString(), "detalhe-solicitacao");
                                                                openModal(row.codigo.toString());
                                                            }}>
                                                                <Typography variant="subtitle1" fontWeight="medium" sx={{ color: "primary.main", fontWeight: "bold", textDecoration: "underline", cursor: "pointer" }}>
                                                                    {row.codigo}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Criado em {row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Por {row.solicitante || "-"}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {row.itens_count} produto{row.itens_count !== 1 ? 's' : ''}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Status status={row.status} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                        <Stack direction="row" justifyContent="flex-end" mt={2}>
                                            <TablePagination
                                                component="div"
                                                count={total}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                rowsPerPage={limit}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                labelRowsPerPage="Linhas por página"
                                                rowsPerPageOptions={[5, 10, 25]}
                                            />
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                <DialogListarSolicitacao
                    open={openFiltros}
                    onClose={() => setOpenFiltros(false)}
                    onConfirm={handleApplyFilters}
                    filtrosAtuais={filtros}
                />
            </Container>
        </Box>
    );
}

export default function ListarSolicitacoes() {
    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Estoque" />
            <PermissionGuard permissionId="estoque.listar-solicitacoes">
                <ModalDetalheSolicitacaoProvider>
                    <ListarSolicitacaoContent />
                </ModalDetalheSolicitacaoProvider>
            </PermissionGuard>
        </>
    );
}

export interface TFiltroListarSolicitacao {
    status?: string[]
    palavraChave?: string;
}

const filtersChipLabels: { [key in keyof Omit<TFiltroListarSolicitacao, 'palavraChave'>]: string } = {
    status: "Status",
};